import { invokeFunction } from "@/lib/functions";
import { supabase } from "@/lib/supabase";
import type { Database } from "@shared/types/supabase.types";
import type {
  CreateContainerInviteInput,
  CreateInviteInput,
  InviteRow,
  ListInvitesParams,
  ListInvitesResult,
} from "../invites.types";

// CR-179: a superuser sign-up invite is redeemable exactly once. Pinned here, not taken
// from the caller, so no UI path can mint a multi-use sign-up invite. Workspace/project
// invites (createContainerInvite) keep their configurable limit.
const SIGNUP_INVITE_MULTIPLE_USE = 1;

export async function createInvite(input: CreateInviteInput = {}): Promise<InviteRow> {
  const {
    email,
    domain,
    canCreateWorkspace = true,
    invitedByContainerMemberId,
    invitedByUserId,
  } = input;

  const normalizedEmail = email?.trim().toLowerCase() || null;
  const normalizedDomain = domain?.trim().toLowerCase() || null;

  const payload: Database["public"]["Tables"]["invites"]["Insert"] = {
    email: normalizedEmail,
    domain: normalizedDomain,
    multiple_use: SIGNUP_INVITE_MULTIPLE_USE,
    invite_type: "signup",
    can_create_workspace: canCreateWorkspace,
    invited_by_cm: invitedByContainerMemberId ?? null,
    invited_by: invitedByUserId ?? null,
  };

  const { data, error } = await supabase.from("invites").insert(payload).select().single();

  if (error) {
    throw new Error(`Failed to create invite: ${error.message}`);
  }

  return data;
}

export async function createContainerInvite(input: CreateContainerInviteInput): Promise<InviteRow> {
  const {
    containerId,
    role,
    email = null,
    domain = null,
    multipleUse = 1,
    canCreateWorkspace = false,
  } = input;

  const normalizedEmail = email?.trim().toLowerCase() || undefined;
  const normalizedDomain = domain?.trim().toLowerCase() || undefined;

  const payload: Database["public"]["Functions"]["create_container_invite"]["Args"] = {
    p_container_id: containerId,
    p_role: role,
    p_email: normalizedEmail,
    p_domain: normalizedDomain,
    p_multiple_use: multipleUse,
    p_can_create_workspace: canCreateWorkspace,
  };

  const { data, error } = await supabase.rpc("create_container_invite", payload);

  if (error) {
    throw new Error(`Failed to create invite: ${error.message}`);
  }

  const invite = data as InviteRow;

  if (invite.email) {
    try {
      const { error: invokeError } = await invokeFunction<{
        success?: boolean;
        skipped?: string;
        reason?: string;
      }>("send-workspace-invite-email", {
        body: {
          inviteId: invite.id,
          containerId,
        },
      });

      if (invokeError) {
        console.warn("create_container_invite: email dispatch failed", {
          inviteId: invite.id,
          containerId,
          message: invokeError.message,
        });
      }
    } catch (invokeError) {
      const errorMessage = invokeError instanceof Error ? invokeError.message : String(invokeError);

      console.warn("create_container_invite: unexpected error sending invite email", {
        inviteId: invite.id,
        containerId,
        error: errorMessage,
      });
    }
  }

  return invite;
}

export async function listInvites(params: ListInvitesParams = {}): Promise<ListInvitesResult> {
  const limit = Math.max(1, params.limit ?? 10);
  const page = Math.max(0, params.page ?? 0);
  const from = page * limit;
  const to = from + limit - 1;
  const status = params.status;
  const searchTerm = params.search?.trim() ?? "";

  const sanitizedSearch = searchTerm.replace(/[%_,]/g, "").toLowerCase();
  const isUuidSearch = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(
    sanitizedSearch,
  );

  let query = supabase.from("invites").select("*", { count: "exact" }).eq("invite_type", "signup");

  if (status === "active") {
    query = query.eq("used", false);
  } else if (status === "used") {
    query = query.eq("used", true);
  }

  if (sanitizedSearch) {
    const domainSearch = sanitizedSearch.replace(/^@+/, "");
    const likePattern = `%${sanitizedSearch}%`;
    const orFilters = [`email.ilike.${likePattern}`];

    if (domainSearch) {
      orFilters.push(`domain.ilike.%${domainSearch}%`);
    }

    if (isUuidSearch) {
      orFilters.push(`code.eq.${sanitizedSearch}`);
    }

    if (orFilters.length > 0) {
      query = query.or(orFilters.join(","));
    }
  }

  const { data, error, count } = await query
    .order("created_at", { ascending: false })
    .range(from, to);

  if (error) {
    throw new Error(`Failed to load invites: ${error.message}`);
  }

  const invites = Array.isArray(data) ? data : [];
  const total = typeof count === "number" ? count : invites.length;

  return {
    invites,
    total,
  };
}

export async function deleteInvites(inviteIds: string[]): Promise<void> {
  if (inviteIds.length === 0) return;

  const { error } = await supabase.from("invites").delete().in("id", inviteIds);

  if (error) {
    throw new Error(`Failed to delete invites: ${error.message}`);
  }
}

// CR-208: a superuser restores the single-use workspace-creation right of one account.
// The error codes come from grant_create_workspace_permission (migration 20261010160000);
// the UI maps each key to its own copy, so no literal here is user-facing (wuchale would
// otherwise rewrite it to a catalog lookup).
export type GrantCreateWorkspaceErrorKey =
  | "not_authorized"
  | "profile_not_found"
  | "ambiguous_email"
  | "unknown";

const GRANT_ERROR_KEYS: Record<string, GrantCreateWorkspaceErrorKey> = {
  "42501": "not_authorized",
  P0002: "profile_not_found",
  "21000": "ambiguous_email",
};

export class GrantCreateWorkspaceError extends Error {
  readonly key: GrantCreateWorkspaceErrorKey;

  constructor(key: GrantCreateWorkspaceErrorKey, message: string) {
    super(message);
    this.key = key;
  }
}

/** Grants create_workspace back to the profile with this email; returns its user id. */
export async function grantCreateWorkspacePermission(email: string): Promise<string> {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    const key: GrantCreateWorkspaceErrorKey = "profile_not_found";
    throw new GrantCreateWorkspaceError(key, key);
  }

  const { data, error } = await supabase.rpc("grant_create_workspace_permission", {
    p_email: trimmedEmail,
  });

  if (error) {
    throw new GrantCreateWorkspaceError(GRANT_ERROR_KEYS[error.code] ?? "unknown", error.message);
  }

  return data;
}
